import React, { useEffect, useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import VastuSubHeader from '../components/VastuSubHeader';
import PageBackground from '../components/PageBackground';
import ConsultantActionModal from './ConsultantActionModal';
import './VastuStyles.css';

const toDateTimeInputValue = (dateValue) => {
  if (!dateValue) return '';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '';
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
};

const ConsultantWorkPage = () => {
  const { isLoggedIn } = useAuth();
  const [work, setWork] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Modal State
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState(null);

  // Sorting State
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'descending' });

  const loadData = async () => {
    setIsLoading(true);
    try {
      const response = await apiService.getVastuConsultantWork();
      setWork(response.data);
    } catch (err) {
      toast.error('Failed to load workspace data.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn) loadData();
  }, [isLoggedIn]);

  // --- Sorting Logic ---
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedConsultations = useMemo(() => {
    let sortableItems = [...(work?.consultations || [])];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        let aValue, bValue;
        switch (sortConfig.key) {
          case 'bookingCode': aValue = a.bookingCode; bValue = b.bookingCode; break;
          case 'createdAt': aValue = new Date(a.createdAt).getTime(); bValue = new Date(b.createdAt).getTime(); break;
          case 'customer': aValue = a.customer?.fullName?.toLowerCase() || ''; bValue = b.customer?.fullName?.toLowerCase() || ''; break;
          case 'status': aValue = a.status; bValue = b.status; break;
          case 'scheduledAt': 
             aValue = a.scheduledAt ? new Date(a.scheduledAt).getTime() : 0; 
             bValue = b.scheduledAt ? new Date(b.scheduledAt).getTime() : 0; 
             break;
          default: aValue = a.createdAt; bValue = b.createdAt;
        }

        if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [work?.consultations, sortConfig]);

  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) return <i className="fas fa-sort sort-icon-inactive"></i>;
    return sortConfig.direction === 'ascending' ? <i className="fas fa-sort-up sort-icon-active"></i> : <i className="fas fa-sort-down sort-icon-active"></i>;
  };

  // --- Handlers ---
  const handleOpenActionModal = (consultation) => {
    setSelectedConsultation(consultation);
    setIsActionModalOpen(true);
  };

  const handleSaveAction = async (consultationId, payload) => {
    try {
      const response = await apiService.updateVastuConsultantWork(consultationId, payload);
      if (response.success) {
        toast.success('Consultation updated successfully.');
        loadData(); // Refresh list
        setIsActionModalOpen(false);
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to save updates.');
    }
  };

  const downloadFile = async (consultationId, file) => {
    try {
      await apiService.downloadVastuConsultantFile(consultationId, file._id, file.originalName);
    } catch (err) {
      toast.error('Failed to download file.');
    }
  };

  if (!isLoggedIn) {
    return <div className="vastu-page"><div className="empty-state">Consultants must login to access this desk.</div></div>;
  }

  const bgUrl = 'https://images.unsplash.com/photo-1558535235-943a5c518b2b?auto=format&fit=crop&w=1920&q=80';

  return (
    <div className="vastu-page">
      <PageBackground imageUrl={bgUrl} />
      <VastuSubHeader />

      <div className="vastu-main-content-wrapper" style={{ marginTop: '2rem' }}>
        
        {/* Consultant Header Banner */}
        {work?.consultant && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '2rem', paddingBottom: '1.5rem', borderBottom: '1px solid #e0d9d0' }}>
            <div style={{ width: '60px', height: '60px', borderRadius: '50%', overflow: 'hidden', border: '2px solid var(--vastu-gold)' }}>
               {work.consultant.profilePhoto?.storagePath ? 
                  <img src={work.consultant.profilePhoto.storagePath} alt="avatar" style={{width:'100%', height:'100%', objectFit:'cover'}}/> : 
                  <div style={{width:'100%', height:'100%', background:'#eee', display:'flex', alignItems:'center', justifyContent:'center'}}>VA</div>
               }
            </div>
            <div>
              <h1 style={{ margin: 0, color: 'var(--vastu-deep)' }}>Welcome, {work.consultant.displayName}</h1>
              <p style={{ margin: 0, color: '#666' }}>Active Consultations: {(work?.consultations || []).filter(c => !['completed', 'report_uploaded', 'cancelled'].includes(c.status)).length}</p>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="loading-spinner">Loading your workspace...</div>
        ) : (
          <div className="vastu-table-wrapper" style={{ background: '#fff', padding: '1rem', borderRadius: '12px' }}>
            <table className="vastu-table admin-data-table sortable-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('bookingCode')}>Booking ID {getSortIcon('bookingCode')}</th>
                  <th onClick={() => requestSort('createdAt')}>Booking Date {getSortIcon('createdAt')}</th>
                  <th onClick={() => requestSort('customer')}>Customer Info {getSortIcon('customer')}</th>
                  <th>Property Details</th>
                  <th>Uploaded Files</th>
                  <th onClick={() => requestSort('status')}>Status & Schedule {getSortIcon('status')}</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedConsultations.map((item) => {
                  const isCompleted = ['completed', 'report_uploaded', 'cancelled'].includes(item.status);
                  
                  return (
                  <tr key={item._id} className={isCompleted ? 'row-completed' : ''}>
                    <td>
                      <strong>{item.bookingCode}</strong><br/>
                      <small style={{ color: isCompleted ? '#888' : 'var(--vastu-green)' }}>{item.consultationType?.name}</small>
                    </td>
                    <td>
                      {new Date(item.createdAt).toLocaleDateString('en-IN')}<br/>
                      <small className="text-muted">{new Date(item.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit'})}</small>
                    </td>
                    <td>
                      {item.customer?.fullName}<br/>
                      <small className="text-muted">{item.customer?.email}</small><br/>
                      <small className="text-muted">{item.customer?.phone}</small>
                    </td>
                    <td>
                      {item.property?.propertyType}<br/>
                      <small className="text-muted">{item.property?.city}, {item.property?.country}</small>
                    </td>
                    <td>
                      {(item.uploadedFiles || []).length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {item.uploadedFiles.map(file => (
                             <button key={file._id} type="button" onClick={() => downloadFile(item._id, file)} style={{ background: 'none', border: 'none', color: isCompleted ? '#777' : 'var(--vastu-blue)', cursor: 'pointer', textAlign: 'left', padding: 0, fontSize: '0.85rem', textDecoration: 'underline' }}>
                               <i className="fas fa-file-pdf"></i> {file.originalName.length > 15 ? file.originalName.substring(0, 15) + '...' : file.originalName}
                             </button>
                          ))}
                        </div>
                      ) : <small>No files yet</small>}
                    </td>
                    <td>
                      <span className={`status-pill status-${item.status.replace(/_/g, '-')}`}>{item.status?.replace(/_/g, ' ')}</span>
                      <br/>
                      {item.scheduledAt && (
                        <small style={{ display: 'block', marginTop: '4px', color: '#555' }}>
                          <i className="far fa-calendar-alt"></i> {new Date(item.scheduledAt).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
                        </small>
                      )}
                    </td>
                    <td>
                      <button className="primary-btn" onClick={() => handleOpenActionModal(item)} style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}>
                        {isCompleted ? 'View' : 'Manage'}
                      </button>
                    </td>
                  </tr>
                )})}
                {sortedConsultations.length === 0 && (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#777' }}>No consultations assigned to you yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConsultantActionModal 
        consultation={selectedConsultation}
        consultantProfile={work?.consultant}
        isOpen={isActionModalOpen}
        onClose={() => setIsActionModalOpen(false)}
        onRefresh={loadData}
      />
    </div>
  );
};

export default ConsultantWorkPage;